import { Table } from 'antd';
import { SearchX, ShieldAlert, Tags } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { AccessorialToolbar } from './AccessorialToolbar';
import { AddAccessorialRateDrawer } from './AddAccessorialRateDrawer';
import { buildAccessorialColumns } from './build-accessorial-columns';
import { ChangeAccessorialRateDrawer } from './ChangeAccessorialRateDrawer';
import type { AccessorialRateView, RateCardView } from '../../../rate-cards.api';
import { todayIso } from '../../../policies/rate-line-status';
import { useEndAccessorialRate } from '../hooks/use-end-accessorial-rate';
import { useAccessorialRates } from '../hooks/use-accessorial-rates';
import { useAccessorialTypes } from '../hooks/use-accessorial-types';
import { useRateCardPermissions } from '../../../hooks/use-rate-card-permissions';

/** Accessorial rates are not proposals: add, change and end each take effect at once, and say so. */
export function AccessorialRatesTab({ card }: { card: RateCardView }) {
  const { list, query, state, isRefreshing, reload } = useAccessorialRates(card.id);
  const types = useAccessorialTypes();
  const { canEdit } = useRateCardPermissions();
  const end = useEndAccessorialRate(reload);
  const [isAdding, setIsAdding] = useState(false);
  const [changing, setChanging] = useState<AccessorialRateView | null>(null);
  const today = todayIso();

  if (state.status === 'loading') return <TableSkeleton label="Loading accessorial rates" />;
  if (state.status !== 'loaded') {
    return (
      <EmptyState
        icon={ShieldAlert}
        title="Couldn't load the accessorial rates"
        description="Something went wrong loading this card's accessorial rates. Try reloading the page."
      />
    );
  }

  const isCurrent = query.current ?? true;
  const columns = buildAccessorialColumns({ list, types, today, canEdit: canEdit && card.isActive, onChange: setChanging, onEnd: end });
  const empty = list.isFiltered ? (
    <EmptyState icon={SearchX} title="No matching rates" description="No accessorial rates match these filters." />
  ) : (
    <EmptyState
      icon={Tags}
      title={isCurrent ? 'Nothing in force today' : 'No earlier rates'}
      description="Waiting, escort and other extra charges for this card appear here."
    />
  );

  return (
    <div className="flex flex-col gap-4">
      <AccessorialToolbar
        isCurrent={isCurrent}
        onToggle={(current) => list.setQuery({ current })}
        onAdd={canEdit && card.isActive ? () => setIsAdding(true) : null}
      />
      <Table
        rowKey="id"
        dataSource={state.page.items}
        columns={columns}
        loading={isRefreshing}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: empty }}
        {...list.tableProps<AccessorialRateView>(state.page.total)}
      />
      <AddAccessorialRateDrawer open={isAdding} rateCardId={card.id} types={types} onAdded={reload} onClose={() => setIsAdding(false)} />
      <ChangeAccessorialRateDrawer
        rate={changing}
        type={types.find((type) => type.code === changing?.accessorialTypeCode)}
        onChanged={reload}
        onClose={() => setChanging(null)}
      />
    </div>
  );
}
